import React, { useEffect, useState } from 'react';
import ConfirmDeleteModalComponent from '../../../../components/ConfirmDeleteModalComponent';
import ButtonComponent from '../../../../components/ButtonComponent';
import TableComponent, {
  IColumnProps,
} from '../../../../components/TableComponent';
import AlarmCentralEditionModal from './AlarmCentralEditionModal';
import { IPaginationParams } from '../../../../services/types';
import ButtonActionsComponent from '../../../../components/ButtonActionsComponent';
import {
  useDeleteAlarmCentralByIdAsync,
  useGetAlarmCentralByIdAsync,
  useGetAllAlarmCentralsByLicenseCountAsync,
} from '../../../../services/AlarmCentralService/hooks/useMutations';
import {
  IAlarmCentralProps,
  IAlarmCentralSimpleProps,
} from '../../../../services/AlarmCentralService/types';
import { SettingsRemote } from '@mui/icons-material';
import IsOnlineComponent from '../../../../components/IsOnlineComponent';
import { getStreamByEndpointAsync } from '../../../../services/Events';
import { ENDPOINT_ALARM_CENTRAL } from '../../../../services/AlarmCentralService';
import { useNavigate } from 'react-router-dom';
import { useGetVirtualAlarmCentralById } from 'services/AlarmCentralService/Virtual/hooks/useMutation';
import { Box, Typography } from '@mui/material';
import UpcomingIcon from '@mui/icons-material/Upcoming';
import AlarmCentralEvents from './AlarmCentralEvents';
import { useTranslation } from 'react-i18next';

const AlarmCentralList = () => {
  const { t } = useTranslation();
  const [isVisibleEditionModal, setIsVisibleEditionModal] = useState(false);
  const navigate = useNavigate();
  const [alarmCentralToEdit, setAlarmCentralToEdit] = useState<
    IAlarmCentralProps | undefined
  >(undefined);
  const [pagination, setPagination] = useState<IPaginationParams>({
    pageNumber: 1,
    pageSize: 10,
  });
  const [isRefetch, setIsRefetch] = useState(false);
  const [alarmCentralsCount, setAlarmCentralsCount] = useState(0);
  const [alarmCentrals, setAlarmCentrals] = useState<
    IAlarmCentralSimpleProps[]
  >([]);
  const [alarmCentralEditionModalKey, setAlarmCentralEditionModalKey] =
    useState(0);

  const [selectedItem, setSelectedItem] = useState<IAlarmCentralSimpleProps>();
  const [isVisibleDeleteItemModal, setIsVisibleDeleteItemModal] =
    useState(false);
  const {
    mutateAsync: mutateDeleteAlarmCentralByIdAsync,
    isLoading: isLoadingDeleteAlarmCentralByIdAsync,
  } = useDeleteAlarmCentralByIdAsync();
  const { mutateAsync: mutateGetAlarmCentralByIdAsync } =
    useGetAlarmCentralByIdAsync();
  const { mutateAsync: mutateGetVirtualAlarmCentralById } =
    useGetVirtualAlarmCentralById();

  const columns: IColumnProps[] = [
    {
      label: t('common.name'),
      id: 'id',
      render(value, rowIndex) {
        const searched = alarmCentrals.find(
          (v) => v.id == Number.parseInt(value),
        );
        if (searched) {
          return (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.3 }}>
              <IsOnlineComponent
                width={18}
                height={18}
                isOnline={Boolean(searched.isConnected)}
              />
              {searched.name}
            </Box>
          );
        }
        return <></>;
      },
    },
    {
      id: 'isVirtual',
      label: t('alarmCentral.type'),
      width: '0%',
      render: (val) => (
        <> {val ? t('alarmCentral.virtual') : t('alarmCentral.physical')}</>
      ),
    },
    {
      id: 'macAddress',
      label: t('alarmCentral.macAddress'),
      width: '0%',
      align: 'center',
      render: (val) => {
        return <>{!val || val == '' ? '-' : val}</>;
      },
    },
    {
      id: 'model',
      label: t('common.model'),
      width: '0%',
      align: 'center',
      render: (val) => {
        return <>{!val || val == '' ? '-' : val}</>;
      },
    },
    {
      id: 'id',
      label: t('common.actions'),
      align: 'center',
      width: '0%',
      render: (id) => (
        <ButtonActionsComponent
          isWithDefaultOptionsList
          actionOptions={actionOptions(Number(id))}
          handleDelete={() => handleDeleteItem(Number(id))}
          handleEdit={() => handleEdit(Number(id))}
        />
      ),
    },
  ];

  const { mutateAsync: mutateGetAllAlarmCentralsByLicenseCountAsync } =
    useGetAllAlarmCentralsByLicenseCountAsync();

  const actionOptions = (id: number) => {
    const alarmCentral = alarmCentrals.find((d) => d.id === id);
    const isDisabled =
      alarmCentral && !alarmCentral.status && !alarmCentral.isVirtual;
    return [
      {
        label: 'Gerenciar',
        disabled: isDisabled,
        icon: <SettingsRemote />,
        onClick: () =>
          alarmCentral?.isVirtual
            ? navigate('virtual/' + id.toString())
            : navigate('physical/' + id.toString()),
      },
    ];
  };

  const onResponseStream = () => {
    mutateGetAllAlarmCentralsByLicenseCountAsync().then(setAlarmCentralsCount);
  };

  const handleEdit = (id: number) => {
    const selectedAlarmCentral = alarmCentrals.find((item) => item.id === id);
    if (selectedAlarmCentral) {
      if (selectedAlarmCentral.isVirtual) {
        mutateGetVirtualAlarmCentralById(id).then((val: any) =>
          setAlarmCentralToEdit({
            ...val,
            isVirtual: true,
            account: val.account,
          }),
        );
      } else mutateGetAlarmCentralByIdAsync(id).then(setAlarmCentralToEdit);
      setIsVisibleEditionModal(true);
    }
  };

  const handleConfirmDeletion = () => {
    if (selectedItem?.id)
      mutateDeleteAlarmCentralByIdAsync(selectedItem?.id).then(() => {
        resetAll();
      });
  };

  const handleDeleteItem = (id: number) => {
    if (alarmCentrals) {
      setSelectedItem(alarmCentrals.find((d) => d.id === id));
      setIsVisibleDeleteItemModal(true);
    }
  };

  const handleCancelDeletion = () => {
    resetAll();
  };

  const additionalComponent = () => {
    return (
      <ButtonComponent onClick={() => setIsVisibleEditionModal(true)}>
        + {t('alarmCentral.newCentral')}
      </ButtonComponent>
    );
  };

  const resetAll = () => {
    setSelectedItem(undefined);
    setIsVisibleEditionModal(false);
    setIsVisibleDeleteItemModal(false);
    setAlarmCentralToEdit(undefined);
    setAlarmCentralEditionModalKey(Math.random() * 20);
    setIsRefetch(!isRefetch);
    // refetchAll();
  };

  useEffect(() => {
    getStreamByEndpointAsync(
      ENDPOINT_ALARM_CENTRAL,
      setAlarmCentrals,
      pagination,
      onResponseStream,
    );
  }, [pagination, isRefetch]);
  return (
    <>
      <TableComponent
        additionalTitleComponent={additionalComponent()}
        rows={alarmCentrals ?? []}
        title={
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <UpcomingIcon color="success" />
            <Typography fontSize={19}>{t('alarmCentral.title')}</Typography>
          </Box>
        }
        totalCount={alarmCentralsCount}
        expandedRowContent={(row: any) => (
          <AlarmCentralEvents alarmCentralId={row.id} />
        )}
        isExpandedRow
        onChangePageOrRowsPerPage={setPagination}
        columns={columns}
      />
      <AlarmCentralEditionModal
        key={alarmCentralEditionModalKey}
        isOpen={isVisibleEditionModal}
        handleClose={resetAll}
        setIsOpen={setIsVisibleEditionModal}
        selectedItem={alarmCentralToEdit}
      />

      <ConfirmDeleteModalComponent
        isLoading={isLoadingDeleteAlarmCentralByIdAsync}
        isVisibleDeleteDialog={isVisibleDeleteItemModal}
        setIsVisibleDeleteDialog={setIsVisibleDeleteItemModal}
        title={t('alarmCentral.deleteTitle')}
        message={t('alarmCentral.deleteMessage', { name: selectedItem?.name })}
        handleCancelDeletion={handleCancelDeletion}
        handleConfirmDeletion={handleConfirmDeletion}
      />
    </>
  );
};

export default AlarmCentralList;
